import React, { useState } from 'react'
import {
  DndContext,
  Modifiers,
  KeyboardSensor,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors
} from '@dnd-kit/core'
import {
  SortableContext,
  arrayMove as reorderItems,
  sortableKeyboardCoordinates
} from '@dnd-kit/sortable'
import {
  restrictToHorizontalAxis,
  restrictToVerticalAxis,
  restrictToWindowEdges
} from '@dnd-kit/modifiers'
import noop from 'lodash/noop'
import SortableItem, { SortableItemProps } from './sortable-item'

type Lock = 'none' | 'x' | 'y'

type AxisLockProps = {
  lock?: Lock
  lockWindow?: boolean
}

// Types for items and setItems props do not need to be the same
// as long as they are linked, we really only care that
// the items are re-order with setItems.
type SortableProps<T extends SortableItemProps> = AxisLockProps & {
  items: T[]
  children: never
  setItems: React.Dispatch<React.SetStateAction<unknown[]>>
  transform: (items: unknown[]) => unknown[]
  onReorder?: (item: Omit<T, 'children'>, newLoc: number) => void
}

const getModifiers = (props: AxisLockProps): Modifiers => {
  const mods = []
  if (props.lock !== 'none')
    mods.push(props.lock === 'x' ? restrictToHorizontalAxis : restrictToVerticalAxis)
  if (props.lockWindow) mods.push(restrictToWindowEdges)
  return mods
}

const indentity = (v: unknown[]) => v

const Sortable = <T extends SortableItemProps>({
  children,
  items = [],
  setItems,
  onReorder = noop,
  lock = 'none',
  lockWindow = true,
  transform = indentity,
  ...rest
}: SortableProps<T>) => {
  const [activeId, setActiveId] = useState<unknown | null>(null)
  const getIndex = (id: unknown) => items.findIndex(el => el.id === id)
  const activeIndex = activeId ? getIndex(activeId) : -1
  const sensors = useSensors(
    useSensor(MouseSensor),
    useSensor(TouchSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
      // Disable smooth scrolling in Cypress automated tests
      scrollBehavior: 'Cypress' in window ? 'auto' : undefined
    })
  )
  return (
    <DndContext
      sensors={sensors}
      modifiers={getModifiers({ lock, lockWindow })}
      onDragStart={({ active }) => {
        if (!active) return
        setActiveId(active.id)
      }}
      onDragEnd={({ over }) => {
        if (over) {
          const overIndex = getIndex(over.id)
          if (activeIndex !== overIndex) {
            const active = items[activeIndex]
            const { children, ...rest } = active
            onReorder(rest, overIndex)
            setItems(items => transform(reorderItems(items, activeIndex, overIndex)))
          }
        }
      }}
    >
      <SortableContext items={items} {...rest}>
        {items.map(({ id, ...rest }) => (
          <SortableItem key={id} id={id} {...rest} />
        ))}
      </SortableContext>
    </DndContext>
  )
}

export default Sortable
